// Asks the token service for a room token (token/lib/http.js describes the endpoint).

export interface TokenRequestBody {
  /** The meeting to join; omitted = a new meeting. */
  room?: string;
  name: string;
  /** vm.lang: the language the participant speaks and wants to hear. */
  lang: string;
  tenant: string;
}

export interface ConnectionDetails {
  serverUrl: string;
  participantToken: string;
  /** The meeting code (the one asked for, or the new one). */
  room: string;
  tenant: string;
}

export class TokenRequestError extends Error {
  constructor(
    readonly status: number,
    readonly fields: readonly string[] = [],
  ) {
    super(`token service answered ${status}`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export async function requestToken(
  url: string,
  body: TokenRequestBody,
  fetchImpl: typeof fetch = fetch,
): Promise<ConnectionDetails> {
  const response = await fetchImpl(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    // Tokens only, no cookies: under versiyonbir.com/v1-meet the site's cookies stay home.
    credentials: 'omit',
    cache: 'no-store',
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const fields = isRecord(payload) && Array.isArray(payload.fields) ? payload.fields.map(String) : [];
    throw new TokenRequestError(response.status, fields);
  }
  if (
    !isRecord(payload) ||
    typeof payload.server_url !== 'string' ||
    typeof payload.participant_token !== 'string' ||
    typeof payload.room !== 'string' ||
    typeof payload.tenant !== 'string'
  ) {
    throw new TokenRequestError(502);
  }
  return {
    serverUrl: payload.server_url,
    participantToken: payload.participant_token,
    room: payload.room,
    tenant: payload.tenant,
  };
}
